// @flow
import ga from 'app/common/lib/ga';
import invalidate from 'app/common/redux/actions/feeds/invalidate';
import logStats from 'app/common/lib/logStats';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { BoardPrivacy } from 'app/packages/pinterest-flow-types/board';
import type { ContentSensitivity } from 'app/packages/pinterest-flow-types/contentSensitivity';
import type { State as Board } from 'app/common/redux/reducers/board';
import type { Thunk } from 'app/common/redux/types';
import { FEED_TYPE } from 'app/common/redux/reducers/feeds';
import { ownProfileBoardsResourceInvalidates } from 'app/common/react/components/profile/ProfileBoards/utils/resources';
import { updateUserPinCount } from 'app/common/redux/actions/user/update';
import { resourceFetch, type ResourceFetch } from 'app/common/redux/actions/resourceFetch';

const GA_CATEGORY_BOARD_CREATE = 'board_create';
const GA_CATEGORY_BOARD_DELETE = 'board_delete';
const GA_CATEGORY_BOARD_EDIT = 'board_edit';

// TODO(bca): keep this in sync with BoardResource
export type FieldSetKey =
  | 'basic'
  | 'board_picker'
  | 'board_pins_grid'
  | 'boards_to_follow'
  | 'summary'
  | 'pin_count'
  | 'detailed'
  | 'create'
  | 'edit'
  | 'thumb'
  | 'grid_item'
  | 'table_row'
  | 'rss'
  | 'aggregated_activity'
  | 'detailed_conversational_board';

type BoardCreateType = {
  category?: null | string,
  collab_board_email?: boolean,
  collaborator_invites_enabled?: boolean,
  description?: string,
  id?: string,
  name: string,
  privacy: BoardPrivacy,
};

export type BoardType = {
  allow_homefeed_recommendations?: boolean,
  category: ?string,
  collab_board_email?: boolean,
  collaborator_invites_enabled?: boolean,
  collaborator_requests_enabled?: boolean,
  description?: string,
  id: string,
  name?: string,
  privacy?: BoardPrivacy,
  sensitivity?: ContentSensitivity,
};

export type FetchAllIfNeeded = {
  type: 'BOARDS_FETCH_ALL_IF_NEEDED',
  payload: {},
};
export function fetchAllIfNeeded() {
  return {
    type: 'BOARDS_FETCH_ALL_IF_NEEDED',
    payload: {},
  };
}

export type FetchLastPinnedToIfNeeded = {
  type: 'BOARDS_FETCH_LAST_PINNED_TO_IF_NEEDED',
  payload: {},
};
export function fetchLastPinnedToIfNeeded() {
  return {
    type: 'BOARDS_FETCH_LAST_PINNED_TO_IF_NEEDED',
    payload: {},
  };
}

export function boardFetch(id: string, fieldSetKey: FieldSetKey): ResourceFetch {
  return resourceFetch('BoardResource', {
    board_id: id,
    field_set_key: fieldSetKey,
  });
}

export type BoardCreateComplete = {
  type: 'BOARD_CREATE_COMPLETE',
  payload: {
    options: Object,
    board: BoardCreateType,
  },
  error?: Error,
};

export function boardCreateComplete(
  options: Object = {},
  board: BoardCreateType,
  error?: any,
): BoardCreateComplete {
  return {
    type: 'BOARD_CREATE_COMPLETE',
    payload: {
      options,
      board,
    },
    error,
  };
}

export function invalidateOwnProfileBoards(): Thunk<void> {
  return (dispatch, getState) => {
    const { viewer } = getState();
    if (!viewer.isAuth) {
      return;
    }
    ownProfileBoardsResourceInvalidates(viewer.username).forEach((action) => {
      dispatch(action);
    });
  };
}

export function createBoard(board: BoardCreateType): Thunk<Promise<Object>> {
  const boardCreateOptions = { ...board };
  return (dispatch) => {
    dispatch({
      type: 'BOARD_CREATE',
      payload: { board },
    });
    return ResourceFactory.create('BoardResource', boardCreateOptions)
      .callCreate()
      .then((response: Object) => {
        ga.trackEvent(GA_CATEGORY_BOARD_CREATE, PCONST.GA_ACTION_SUCCESS);
        const board = response.resource_response.data;
        if (board) {
          dispatch(invalidateOwnProfileBoards());
          dispatch(boardCreateComplete(boardCreateOptions, board));
        }
        return board;
      })
      .catch((error) => {
        ga.trackEvent(GA_CATEGORY_BOARD_CREATE, PCONST.GA_ACTION_ERROR);
        throw error;
      });
  };
}

export type BoardUpdateComplete = {
  type: 'BOARD_UPDATE_COMPLETE',
  payload: {
    options: Object,
    board: BoardType,
  },
  error?: Error,
};

export function boardUpdateComplete(
  options: Object = {},
  board: BoardType,
  error?: any,
): BoardUpdateComplete {
  return {
    type: 'BOARD_UPDATE_COMPLETE',
    payload: {
      options,
      board,
    },
    error,
  };
}

export function updateBoard(board: BoardType): Thunk<Promise<void>> {
  return (dispatch) => {
    const optionalCollabRequestsEnabled =
      board.collaborator_requests_enabled === undefined ||
      board.collaborator_requests_enabled === null
        ? {}
        : { collaborator_requests_enabled: board.collaborator_requests_enabled };

    const boardUpdateOptions = {
      board_id: board.id,
      name: board.name,
      description: board.description,
      category: board.category,
      privacy: board.privacy,
      collab_board_email: board.collab_board_email,
      collaborator_invites_enabled: board.collaborator_invites_enabled,
      allow_homefeed_recommendations: board.allow_homefeed_recommendations,
      ...optionalCollabRequestsEnabled,
    };
    dispatch({
      type: 'BOARD_UPDATE',
      payload: { boardUpdateOptions },
    });
    return ResourceFactory.create('BoardResource', boardUpdateOptions)
      .callUpdate()
      .then((response: any) => {
        ga.trackEvent(GA_CATEGORY_BOARD_EDIT, PCONST.GA_ACTION_SUCCESS);
        const updatedBoard =
          (response && response.resource_response && response.resource_response.data) || board;
        dispatch(boardUpdateComplete(boardUpdateOptions, updatedBoard));
      })
      .catch((error) => {
        ga.trackEvent(GA_CATEGORY_BOARD_EDIT, PCONST.GA_ACTION_ERROR);
        throw error;
      });
  };
}

export type BoardUpdateCollaborationSettings = {
  type: 'BOARD_UPDATE_COLLABORATION_SETTINGS',
  payload: { boardId: string, collaboratorRequestsEnabled: boolean },
};

export function boardUpdateCollaborationSettings(
  boardId: string,
  collaboratorRequestsEnabled: boolean,
): BoardUpdateCollaborationSettings {
  return {
    type: 'BOARD_UPDATE_COLLABORATION_SETTINGS',
    payload: {
      boardId,
      collaboratorRequestsEnabled,
    },
  };
}

export type BoardDeleteComplete = {
  type: 'BOARD_DELETE_COMPLETE',
  payload: { boardId: string },
  error?: Error,
};
export function boardDeleteComplete(boardId: string, error?: any): BoardDeleteComplete {
  return {
    type: 'BOARD_DELETE_COMPLETE',
    payload: { boardId },
    error,
  };
}

export function deleteBoard(boardId: string): Thunk<Promise<void>> {
  return (dispatch, getState) => {
    const boardToDelete = getState().boards.content[boardId];
    const { pin_count: boardPinCount } = boardToDelete;
    const { id: boardOwnerId } = boardToDelete.owner;

    dispatch({
      type: 'BOARD_DELETE',
      payload: { boardId },
    });
    return ResourceFactory.create('BoardResource', { board_id: boardId })
      .callDelete()
      .then(() => {
        ga.trackEvent(GA_CATEGORY_BOARD_DELETE, PCONST.GA_ACTION_ERROR);
        dispatch(boardDeleteComplete(boardId));
        dispatch(updateUserPinCount({ userId: boardOwnerId, increment: -boardPinCount }));
      })
      .catch((error) => {
        ga.trackEvent(GA_CATEGORY_BOARD_DELETE, PCONST.GA_ACTION_ERROR);
        throw error;
      });
  };
}

export type BoardPinTo = {
  type: 'BOARD_PIN_TO',
  payload: {
    board: Board,
  },
};
export function boardPinTo(board: Board): BoardPinTo {
  return {
    type: 'BOARD_PIN_TO',
    payload: { board },
  };
}

export type BoardArchived = {
  type: 'BOARD_ARCHIVED',
  payload: {
    boardId: string,
  },
};

function boardArchived(boardId: string): BoardArchived {
  return {
    type: 'BOARD_ARCHIVED',
    payload: {
      boardId,
    },
  };
}

export function archiveBoard(boardId: string): Thunk<Promise<any>> {
  return (dispatch) => {
    dispatch(boardArchived(boardId)); // optimistically mark board as archived
    return ResourceFactory.create('BoardArchiveResource', {
      boardId,
    }).callUpdate();
  };
}

export type BoardUnarchived = {
  type: 'BOARD_UNARCHIVED',
  payload: {
    boardId: string,
  },
};

function boardUnarchived(boardId: string): BoardUnarchived {
  return {
    type: 'BOARD_UNARCHIVED',
    payload: {
      boardId,
    },
  };
}

export function unarchiveBoard(boardId: string): Thunk<Promise<any>> {
  return (dispatch) => {
    dispatch(boardUnarchived(boardId)); // optimistically mark board as unarchived
    return ResourceFactory.create('BoardArchiveResource', {
      boardId,
    }).callDelete();
  };
}

export type BoardRestored = {
  type: 'BOARD_RESTORED',
  payload: {
    boardId: string,
  },
};

function boardRestored(boardId: string): BoardRestored {
  return {
    type: 'BOARD_RESTORED',
    payload: {
      boardId,
    },
  };
}

export function restoreBoard(boardId: string): Thunk<Promise<any>> {
  return (dispatch) => {
    dispatch(boardRestored(boardId)); // optimistically mark board as restored
    return ResourceFactory.create('BoardSoftDeleteResource', {
      boardId,
    }).callUpdate();
  };
}

export function mergeBoards(
  sourceBoardId: string,
  targetBoardId: string,
): Thunk<Promise<Array<void>>> {
  return (dispatch) =>
    ResourceFactory.create('BoardsMergeResource', {
      source_board_id: sourceBoardId,
      target_board_id: targetBoardId,
    })
      .callUpdate()
      .then(() =>
        Promise.all([
          // optimistically removing the source board from redux
          dispatch(boardDeleteComplete(sourceBoardId)),

          // invalidate target board cache
          dispatch(boardFetch(targetBoardId, 'pin_count')),
          dispatch(invalidate(FEED_TYPE.BOARD_SECTIONS, targetBoardId)),
        ]),
      );
}

export type SeasonalBoard = {
  type: 'SET_SOCIAL_SEASONAL_BOARD',
  payload: {
    isSocialSeasonalBoard: boolean,
    socialHeaderBgImage: string,
    socialHeaderBgColor: string,
    textIconColor: 'white' | 'darkGray',
  },
};
export function setSocialSeasonalBoard(
  boardId: number | string,
  authStatus: 'auth' | 'unauth',
): Thunk<Promise<SeasonalBoard>> {
  return (dispatch) =>
    ResourceFactory.create('SocialSearchCampaignResource', {
      boardId,
    })
      .callGet()
      .then((response) => {
        if (!response || !response.resource_response) {
          const dispatchData = {
            isSocialSeasonalBoard: false,
            socialHeaderBgImage: '',
            socialHeaderBgColor: '',
            textIconColor: '',
          };

          return dispatch({
            type: 'SET_SOCIAL_SEASONAL_BOARD',
            payload: dispatchData,
          });
        }

        const data = response.resource_response.data || {};
        const customProperties = (data.model || {}).custom_properties || {};
        const { campaign_id = 'unknown' } = customProperties;
        const displayData = data.display_data || {};
        const isSocialSeasonalBoard = !!data.display_data;
        const socialHeaderBgImage = displayData.header_image;
        const socialHeaderBgColor = displayData.header_background_color;
        const textIconColor = displayData.text_icon_color;
        const dispatchData = {
          isSocialSeasonalBoard,
          socialHeaderBgImage,
          socialHeaderBgColor,
          textIconColor,
        };

        logStats.increment(`web.seasonal_board.${campaign_id}.${authStatus}.land`);
        return dispatch({
          type: 'SET_SOCIAL_SEASONAL_BOARD',
          payload: dispatchData,
        });
      });
}

export function clearSocialSeasonalBoard(): Thunk<Promise<SeasonalBoard>> {
  return (dispatch) => {
    const dispatchData = {
      isSocialSeasonalBoard: false,
      socialHeaderBgImage: '',
      socialHeaderBgColor: '',
      textIconColor: '',
    };

    return dispatch({
      type: 'SET_SOCIAL_SEASONAL_BOARD',
      payload: dispatchData,
    });
  };
}
